import { useState, useEffect, useCallback, useMemo } from 'react';
import { MediaPermissionsStatus, PermissionState, MediaDeviceType } from '../types';
import {
  checkMediaPermissions,
  checkCameraPermission,
  checkMicrophonePermission,
  areMediaPermissionsGranted,
  needsPermissionPrompt,
} from '../utils/permission-checker';

// Base hook for permission checking with shared logic
interface UsePermissionCheckResult<T> {
  result: T | null;
  isLoading: boolean;
  error: string | null;
  check: () => Promise<void>;
}

function usePermissionCheck<T>(
  checkFunction: () => Promise<T>,
  errorMessage: string,
  autoCheck: boolean = true,
): UsePermissionCheckResult<T> {
  const [result, setResult] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await checkFunction();
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : errorMessage);
      setResult(null);
    } finally {
      setIsLoading(false);
    }
  }, [checkFunction, errorMessage]);

  useEffect(() => {
    if (autoCheck) {
      check();
    }
  }, [autoCheck, check]);

  return { result, isLoading, error, check };
}

// Utility function for computed permission properties
function getPermissionHelpers(permission: PermissionState | null) {
  return {
    isGranted: permission === PermissionState.GRANTED,
    needsPrompt: permission === PermissionState.PROMPT || permission === PermissionState.UNKNOWN,
  };
}

// Generic result type for specific permission hooks
interface UseSpecificPermissionResult {
  permission: PermissionState | null;
  isLoading: boolean;
  error: string | null;
  checkPermission: () => Promise<void>;
  isGranted: boolean;
  needsPrompt: boolean;
}

// Generic hook for specific permission types (camera/microphone)
function useSpecificPermission(
  checkFunction: () => Promise<PermissionState>,
  errorMessage: string,
  autoCheck: boolean = true,
): UseSpecificPermissionResult {
  const {
    result: permission,
    isLoading,
    error,
    check: checkPermission,
  } = usePermissionCheck(checkFunction, errorMessage, autoCheck);

  const { isGranted, needsPrompt } = getPermissionHelpers(permission);

  return {
    permission,
    isLoading,
    error,
    checkPermission,
    isGranted,
    needsPrompt,
  };
}

export interface UseMediaPermissionsResult {
  permissions: MediaPermissionsStatus | null;
  isLoading: boolean;
  error: string | null;
  checkPermissions: (forceFallback?: boolean) => Promise<void>;
  refresh: (forceFallback?: boolean) => Promise<void>;
}

/**
 * Custom hook for checking and managing media permissions
 */
export function useMediaPermissions(autoCheck: boolean = true): UseMediaPermissionsResult {
  const [permissions, setPermissions] = useState<MediaPermissionsStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkPermissions = useCallback(async (forceFallback: boolean = false): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const permissionResult = await checkMediaPermissions(forceFallback);

      setPermissions(permissionResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check permissions');
      setPermissions(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refresh = useCallback(
    async (forceFallback: boolean = false): Promise<void> => {
      await checkPermissions(forceFallback);
    },
    [checkPermissions],
  );

  // Auto-check permissions on mount if enabled
  useEffect(() => {
    if (autoCheck) {
      checkPermissions();
    }
  }, [autoCheck, checkPermissions]);

  return {
    permissions,
    isLoading,
    error,
    checkPermissions,
    refresh,
  };
}

// Type alias for backwards compatibility
export type UseCameraPermissionResult = UseSpecificPermissionResult;

/**
 * Custom hook specifically for camera permissions
 */
export function useCameraPermission(autoCheck: boolean = true): UseCameraPermissionResult {
  return useSpecificPermission(
    checkCameraPermission,
    'Failed to check camera permission',
    autoCheck,
  );
}

// Type alias for backwards compatibility
export type UseMicrophonePermissionResult = UseSpecificPermissionResult;

/**
 * Custom hook specifically for microphone permissions
 */
export function useMicrophonePermission(autoCheck: boolean = true): UseMicrophonePermissionResult {
  return useSpecificPermission(
    checkMicrophonePermission,
    'Failed to check microphone permission',
    autoCheck,
  );
}

export interface UsePermissionPromptResult {
  needsPrompt: Record<MediaDeviceType, boolean> | null;
  allGranted: boolean | null;
  isLoading: boolean;
  error: string | null;
  checkPrompts: () => Promise<void>;
}

/**
 * Custom hook for checking if permission prompts are needed
 */
export function usePermissionPrompt(autoCheck: boolean = true): UsePermissionPromptResult {
  const {
    result,
    isLoading,
    error,
    check: checkPrompts,
  } = usePermissionCheck(
    async () => {
      const [promptResult, grantedResult] = await Promise.all([
        needsPermissionPrompt(),
        areMediaPermissionsGranted(),
      ]);
      return { needsPrompt: promptResult, allGranted: grantedResult };
    },
    'Failed to check permission prompts',
    autoCheck,
  );

  return {
    needsPrompt: result?.needsPrompt ?? null,
    allGranted: result?.allGranted ?? null,
    isLoading,
    error,
    checkPrompts,
  };
}
